import { RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CameraState = 'initializing' | 'ready' | 'captured' | 'error';
type FrameAspect = 'landscape' | 'portrait';

interface CameraViewfinderProps {
  documentType: string;
  onCapture: (file: File) => void;
  onClose: () => void;
}

const DOCUMENT_FRAME_CONFIG: Record<string, FrameAspect> = {
  drivers_license: 'landscape',
  state_id: 'landscape',
  passport: 'landscape',
  social_security: 'landscape',
  class_d_license: 'portrait',
  class_g_license: 'portrait',
  guard_card: 'portrait',
  cpr_aed_cert: 'portrait',
  twic_card: 'landscape',
  work_authorization: 'portrait',
};

const LANDSCAPE_RATIO = 1.586;
const PORTRAIT_RATIO = 0.773;

function getFrameAspect(documentType: string): FrameAspect {
  return DOCUMENT_FRAME_CONFIG[documentType] ?? 'landscape';
}

function computeFrameRect(
  containerWidth: number,
  containerHeight: number,
  aspect: FrameAspect,
): { x: number; y: number; width: number; height: number } {
  const paddingTop = 80;
  const paddingBottom = 120;
  const paddingX = containerWidth * 0.075;

  const availableWidth = containerWidth - paddingX * 2;
  const availableHeight = containerHeight - paddingTop - paddingBottom;
  const ratio = aspect === 'landscape' ? LANDSCAPE_RATIO : PORTRAIT_RATIO;

  let frameWidth = availableWidth;
  let frameHeight = frameWidth / ratio;

  if (frameHeight > availableHeight) {
    frameHeight = availableHeight;
    frameWidth = frameHeight * ratio;
  }

  const x = (containerWidth - frameWidth) / 2;
  const y = paddingTop + (availableHeight - frameHeight) / 2;

  return { x, y, width: frameWidth, height: frameHeight };
}

function CornerMarkers({ x, y, width, height }: { x: number; y: number; width: number; height: number }) {
  const armLength = 24;
  const thickness = 3;
  const color = 'white';

  return (
    <svg className='absolute inset-0 w-full h-full pointer-events-none' style={{ zIndex: 2 }}>
      {/* Top-left */}
      <rect x={x} y={y} width={armLength} height={thickness} fill={color} rx={1} />
      <rect x={x} y={y} width={thickness} height={armLength} fill={color} rx={1} />

      {/* Top-right */}
      <rect x={x + width - armLength} y={y} width={armLength} height={thickness} fill={color} rx={1} />
      <rect x={x + width - thickness} y={y} width={thickness} height={armLength} fill={color} rx={1} />

      {/* Bottom-left */}
      <rect x={x} y={y + height - thickness} width={armLength} height={thickness} fill={color} rx={1} />
      <rect x={x} y={y + height - armLength} width={thickness} height={armLength} fill={color} rx={1} />

      {/* Bottom-right */}
      <rect
        x={x + width - armLength}
        y={y + height - thickness}
        width={armLength}
        height={thickness}
        fill={color}
        rx={1}
      />
      <rect
        x={x + width - thickness}
        y={y + height - armLength}
        width={thickness}
        height={armLength}
        fill={color}
        rx={1}
      />
    </svg>
  );
}

function DarkenedOverlay({
  x,
  y,
  width,
  height,
  containerWidth,
  containerHeight,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  containerWidth: number;
  containerHeight: number;
}) {
  return (
    <svg className='absolute inset-0 w-full h-full pointer-events-none' style={{ zIndex: 1 }}>
      <defs>
        <mask id='viewfinder-mask'>
          <rect x={0} y={0} width={containerWidth} height={containerHeight} fill='white' />
          <rect x={x} y={y} width={width} height={height} fill='black' rx={8} />
        </mask>
      </defs>
      <rect
        x={0}
        y={0}
        width={containerWidth}
        height={containerHeight}
        fill='rgba(0,0,0,0.5)'
        mask='url(#viewfinder-mask)'
      />
    </svg>
  );
}

export function CameraViewfinder({ documentType, onCapture, onClose }: CameraViewfinderProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameraState, setCameraState] = useState<CameraState>('initializing');
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [errorType, setErrorType] = useState<'denied' | 'unavailable'>('unavailable');

  const frameAspect = getFrameAspect(documentType);
  const frame = computeFrameRect(containerSize.width, containerSize.height, frameAspect);

  // Check for multiple cameras
  useEffect(() => {
    const checkCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch {
        // Ignore — will show single camera mode
      }
    };
    void checkCameras();
  }, []);

  // Track container size
  useEffect(() => {
    const updateSize = () => {
      setContainerSize({ width: window.innerWidth, height: window.innerHeight });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Start camera stream
  const startStream = useCallback(async (facing: 'environment' | 'user') => {
    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setCameraState('initializing');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setCameraState('ready');
    } catch (err) {
      if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
        setErrorType('denied');
      } else {
        setErrorType('unavailable');
      }
      setCameraState('error');
    }
  }, []);

  useEffect(() => {
    void startStream(facingMode);

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [facingMode, startStream]);

  // Clean up captured URL on unmount
  useEffect(() => {
    return () => {
      if (capturedUrl) {
        URL.revokeObjectURL(capturedUrl);
      }
    };
  }, [capturedUrl]);

  // Lock body scroll while viewfinder is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const handleShutter = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.drawImage(video, 0, 0);

    // Flash effect
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 150);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        if (capturedUrl) {
          URL.revokeObjectURL(capturedUrl);
        }

        setCapturedBlob(blob);
        setCapturedUrl(URL.createObjectURL(blob));
        setCameraState('captured');

        // Pause video stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => {
            track.enabled = false;
          });
        }
      },
      'image/jpeg',
      0.92,
    );
  }, [capturedUrl]);

  const handleRetake = useCallback(() => {
    if (capturedUrl) {
      URL.revokeObjectURL(capturedUrl);
    }
    setCapturedUrl(null);
    setCapturedBlob(null);

    // Re-enable video tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.enabled = true;
      });
    }

    setCameraState('ready');
  }, [capturedUrl]);

  const handleUsePhoto = useCallback(() => {
    if (!capturedBlob) {
      return;
    }

    const timestamp = Date.now();
    const file = new File([capturedBlob], `document-${timestamp}.jpg`, { type: 'image/jpeg' });
    onCapture(file);
  }, [capturedBlob, onCapture]);

  const handleFlipCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));

    // If we were in captured state, go back to ready
    if (capturedUrl) {
      URL.revokeObjectURL(capturedUrl);
    }
    setCapturedUrl(null);
    setCapturedBlob(null);
  }, [capturedUrl]);

  const handleClose = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    onClose();
  }, [onClose]);

  return (
    <div ref={containerRef} className='fixed inset-0 z-[60] bg-black flex flex-col'>
      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} className='hidden' />

      {/* Flash effect */}
      {showFlash && (
        <div className='absolute inset-0 bg-white animate-[fadeOut_150ms_ease-out_forwards]' style={{ zIndex: 10 }} />
      )}

      {/* Camera stream / Captured image */}
      {cameraState !== 'error' && (
        <>
          <video
            ref={videoRef}
            className='absolute inset-0 w-full h-full object-cover'
            autoPlay
            playsInline
            muted
            style={{ display: cameraState === 'captured' ? 'none' : 'block' }}
          />

          {capturedUrl && cameraState === 'captured' && (
            <img src={capturedUrl} alt='' className='absolute inset-0 w-full h-full object-cover' />
          )}
        </>
      )}

      {/* Overlay + markers (only in ready/initializing state) */}
      {(cameraState === 'ready' || cameraState === 'initializing') && containerSize.width > 0 && (
        <>
          <DarkenedOverlay
            x={frame.x}
            y={frame.y}
            width={frame.width}
            height={frame.height}
            containerWidth={containerSize.width}
            containerHeight={containerSize.height}
          />
          <CornerMarkers x={frame.x} y={frame.y} width={frame.width} height={frame.height} />
        </>
      )}

      {/* Top controls */}
      <div className='absolute top-0 left-0 right-0 flex items-center justify-between p-4' style={{ zIndex: 3 }}>
        <button
          type='button'
          onClick={handleClose}
          className='p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition'>
          <X size={24} />
        </button>

        {hasMultipleCameras && cameraState === 'ready' && (
          <button
            type='button'
            onClick={handleFlipCamera}
            className='p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition'
            aria-label={t('documents.camera.flipCamera')}>
            <RefreshCw size={20} />
          </button>
        )}
      </div>

      {/* Hint text (ready state) */}
      {cameraState === 'ready' && (
        <div className='absolute top-14 left-0 right-0 text-center' style={{ zIndex: 3 }}>
          <p className='text-white text-sm font-medium drop-shadow-lg'>{t('documents.camera.alignDocument')}</p>
        </div>
      )}

      {/* Initializing spinner */}
      {cameraState === 'initializing' && (
        <div className='absolute inset-0 flex items-center justify-center' style={{ zIndex: 3 }}>
          <div className='w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin' />
        </div>
      )}

      {/* Error state */}
      {cameraState === 'error' && (
        <div className='absolute inset-0 flex flex-col items-center justify-center gap-4 px-6' style={{ zIndex: 3 }}>
          <p className='text-white text-center text-lg font-medium'>
            {errorType === 'denied' ? t('documents.camera.accessDenied') : t('documents.camera.notAvailable')}
          </p>
          <button
            type='button'
            onClick={handleClose}
            className='px-6 py-3 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition'>
            {t('documents.camera.chooseFileInstead')}
          </button>
        </div>
      )}

      {/* Bottom controls */}
      <div className='absolute bottom-0 left-0 right-0 pb-10 px-6' style={{ zIndex: 3 }}>
        {cameraState === 'ready' && (
          <div className='flex items-center justify-center'>
            <button
              type='button'
              onClick={handleShutter}
              className='w-[72px] h-[72px] rounded-full border-4 border-white bg-white/20 hover:bg-white/30 active:scale-95 transition-transform'
              aria-label={t('documents.camera.shutterLabel')}>
              <div className='w-full h-full rounded-full border-2 border-white/50' />
            </button>
          </div>
        )}

        {cameraState === 'captured' && (
          <div className='flex items-center justify-between gap-4'>
            <button
              type='button'
              onClick={handleRetake}
              className='flex-1 py-3 border border-white text-white rounded-lg font-medium hover:bg-white/10 transition'>
              {t('documents.camera.retake')}
            </button>
            <button
              type='button'
              onClick={handleUsePhoto}
              className='flex-1 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition'>
              {t('documents.camera.usePhoto')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
